import config from 'cio/config/environment';
import { DataCenter } from 'cio/models/account';

export type CioApi = 'track' | 'api' | 'mail';

export const SQL_SERVER_IPS: Record<DataCenter, string> = {
  us: '34.122.196.49',
  eu: '104.155.37.221',
};

export function getApiHost(api: CioApi, dc: DataCenter) {
  let API_HOSTS: Record<CioApi, Record<DataCenter, string>> = {
    track: {
      [DataCenter.US]: 'track',
      [DataCenter.EU]: 'track-eu',
    },
    api: {
      [DataCenter.US]: 'api',
      [DataCenter.EU]: 'api-eu',
    },
    mail: {
      [DataCenter.US]: 'e',
      [DataCenter.EU]: 'e-eu',
    },
  };

  return API_HOSTS[api][dc];
}

export function getHostForDataCenter(dc: DataCenter) {
  // List of the datacenter hosts we support, used to decide the host in the
  // application adapter or the base URL for API requests elsewhere.
  //
  // An empty string (or window.origin) means that the host is the current URL,
  // and all URLs are built relative to it.
  let DATA_CENTER_HOSTS: Record<(typeof config)['cioEnvironment'], Record<DataCenter, string>> = {
    production: {
      [DataCenter.US]: 'https://fly.customer.io',
      [DataCenter.EU]: 'https://fly-eu.customer.io',
    },
    standalone: {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: window.origin,
    },
    'iac-standalone': {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: window.origin,
    },
    'cio-staging': {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: window.origin,
    },
    devbox: {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: window.origin,
    },
    'prod-staging-1': {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: window.origin,
    },
    'prod-staging-2': {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: window.origin,
    },
    test: {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: 'http://fly-eu.cio.test',
    },
    development: {
      [DataCenter.US]: window.origin,
      [DataCenter.EU]: 'http://fly-eu.cio.test',
    },
  };

  return DATA_CENTER_HOSTS[config.cioEnvironment][dc];
}

function transformOriginToTrackOrLocalhost(location: Location) {
  // This captures fly-devbox-xxx.customerio.dev (devboxes)
  if (location.host.startsWith('fly-')) {
    let trackHost = location.host.replace('fly-', 'track-');
    return `${location.protocol}//${trackHost}`;
  }
  // This captures fly.<domain>.customerio.dev (gitpod with custom domain)
  if (location.host.startsWith('fly.')) {
    let trackHost = location.host.replace('fly.', 'track.');
    return `${location.protocol}//${trackHost}`;
  }

  // gitpods without custom domain
  if (location.origin.includes('gitpod.io')) {
    // pure gitpod URLs look like
    // https://13123-customerio-devenvironmen-oulga6vh4wd.ws-us34xl.gitpod.io/
    // where 13123 is the service port.
    // This replaces the port section with the edge port
    // https:\/\/ - match https://
    // [0-9]+ - match any number of digits
    // -[^\s]+.gitpod.io) - match a dash (-) and then any characters until .gitpod.io (including it)
    //
    // $1 reuses the first capture group (https://) in the resulting string,
    // $3 the third capture group (the section with .gitpod.io at the end)
    return location.origin.replace(/(https:\/\/)([0-9]+)(-[^\s]+.gitpod.io)/, '$19100$3');
  }

  // devbox environments via tailscale, like http://devbox-content:13123,
  // or localhost:13123
  return `${location.protocol}//${location.hostname}:9100`;
}

export function getFormsHostForDataCenter(dc: DataCenter) {
  // Forms addresses use different TLD than "customer.io", need their own function:

  let DATA_CENTER_HOSTS: Record<(typeof config)['cioEnvironment'], Record<DataCenter, string>> = {
    production: {
      [DataCenter.US]: 'https://customerioforms.com',
      [DataCenter.EU]: 'https://eu.customerioforms.com',
    },
    'prod-staging-1': {
      [DataCenter.US]: 'https://customerioforms.com',
      [DataCenter.EU]: 'https://eu.customerioforms.com',
    },
    'prod-staging-2': {
      [DataCenter.US]: 'https://customerioforms.com',
      [DataCenter.EU]: 'https://eu.customerioforms.com',
    },
    test: {
      // let's use prod. hostnames in tests:
      [DataCenter.US]: 'https://customerioforms.com',
      [DataCenter.EU]: 'https://eu.customerioforms.com',
    },
    standalone: {
      [DataCenter.US]: 'https://staging.customerioforms.com',
      [DataCenter.EU]: 'https://staging.customerioforms.com',
    },
    'iac-standalone': {
      [DataCenter.US]: 'https://staging.customerioforms.com',
      [DataCenter.EU]: 'https://staging.customerioforms.com',
    },
    'cio-staging': {
      [DataCenter.US]: 'https://staging.customerioforms.com',
      [DataCenter.EU]: 'https://staging.customerioforms.com',
    },
    devbox: {
      [DataCenter.US]: transformOriginToTrackOrLocalhost(window.location),
      [DataCenter.EU]: transformOriginToTrackOrLocalhost(window.location),
    },
    development: {
      [DataCenter.US]: transformOriginToTrackOrLocalhost(window.location),
      [DataCenter.EU]: transformOriginToTrackOrLocalhost(window.location),
    },
  };

  return DATA_CENTER_HOSTS[config.cioEnvironment][dc];
}

export function getTrackApiHostForDataCenter(dc: DataCenter) {
  let DATA_CENTER_HOSTS: Record<(typeof config)['cioEnvironment'], Record<DataCenter, string>> = {
    production: {
      [DataCenter.US]: 'https://track.customer.io',
      [DataCenter.EU]: 'https://track-eu.customer.io',
    },
    'prod-staging-1': {
      [DataCenter.US]: 'https://track.customer.io',
      [DataCenter.EU]: 'https://track-eu.customer.io',
    },
    'prod-staging-2': {
      [DataCenter.US]: 'https://track.customer.io',
      [DataCenter.EU]: 'https://track-eu.customer.io',
    },
    test: {
      // let's use prod. hostnames in tests:
      [DataCenter.US]: 'https://track.customer.io',
      [DataCenter.EU]: 'https://track-eu.customer.io',
    },
    standalone: {
      [DataCenter.US]: 'https://track-gce-staging.customer.io',
      [DataCenter.EU]: 'https://track-gce-staging.customer.io',
    },
    'iac-standalone': {
      [DataCenter.US]: 'https://track-gce-staging.customer.io',
      [DataCenter.EU]: 'https://track-gce-staging.customer.io',
    },
    'cio-staging': {
      [DataCenter.US]: 'https://track-gce-staging.customer.io',
      [DataCenter.EU]: 'https://track-gce-staging.customer.io',
    },
    devbox: {
      [DataCenter.US]: transformOriginToTrackOrLocalhost(window.location),
      [DataCenter.EU]: transformOriginToTrackOrLocalhost(window.location),
    },
    development: {
      [DataCenter.US]: transformOriginToTrackOrLocalhost(window.location),
      [DataCenter.EU]: transformOriginToTrackOrLocalhost(window.location),
    },
  };

  return DATA_CENTER_HOSTS[config.cioEnvironment][dc];
}

export function getAppApiHostForDataCenter(dc: DataCenter) {
  let DATA_CENTER_HOSTS: Record<(typeof config)['cioEnvironment'], Record<DataCenter, string>> = {
    production: {
      [DataCenter.US]: 'https://api.customer.io',
      [DataCenter.EU]: 'https://api-eu.customer.io',
    },
    'prod-staging-1': {
      [DataCenter.US]: 'https://api.customer.io',
      [DataCenter.EU]: 'https://api-eu.customer.io',
    },
    'prod-staging-2': {
      [DataCenter.US]: 'https://api.customer.io',
      [DataCenter.EU]: 'https://api-eu.customer.io',
    },
    test: {
      // let's use prod. hostnames in tests:
      [DataCenter.US]: 'https://api.customer.io',
      [DataCenter.EU]: 'https://api-eu.customer.io',
    },
    standalone: {
      [DataCenter.US]: 'https://track-gce-staging.customer.io',
      [DataCenter.EU]: 'https://track-gce-staging.customer.io',
    },
    'iac-standalone': {
      [DataCenter.US]: 'https://track-gce-staging.customer.io',
      [DataCenter.EU]: 'https://track-gce-staging.customer.io',
    },
    'cio-staging': {
      [DataCenter.US]: 'https://track-gce-staging.customer.io',
      [DataCenter.EU]: 'https://track-gce-staging.customer.io',
    },
    devbox: {
      [DataCenter.US]: transformOriginToTrackOrLocalhost(window.location),
      [DataCenter.EU]: transformOriginToTrackOrLocalhost(window.location),
    },
    development: {
      [DataCenter.US]: transformOriginToTrackOrLocalhost(window.location),
      [DataCenter.EU]: transformOriginToTrackOrLocalhost(window.location),
    },
  };

  return DATA_CENTER_HOSTS[config.cioEnvironment][dc];
}
